export type StoreFeatureFlags = {
  discount_max_percent?: number;
  discount_require_pin_above?: number;
  discount_require_reason?: boolean;
  void_allowed_roles?: string[];
  refund_allowed_roles?: string[];
  price_override_allowed?: boolean;
  price_override_min_role?: string;
  price_override_require_pin?: boolean;
  show_cost_prices_roles?: string[];
  show_profit_margin_roles?: string[];
  show_supplier_costs?: boolean;
  dashboard_min_role?: string;
  reports_min_role?: string;
  financial_reports_min_role?: string;
  can_edit_customer_roles?: string[];
  can_delete_customer_roles?: string[];
  /** Roles that can toggle "Show archived" and view archived customer profiles */
  customer_archive_view_roles?: string[];
  /** Roles that can view archived inventory items in list/detail */
  inventory_archive_view_roles?: string[];
  inventory_adjustment_min_role?: string;
  can_receive_po_roles?: string[];
  cash_session_min_role?: string;
  can_view_cash_report?: string[];
  can_change_promised_date?: boolean;
  can_waive_deposit?: boolean;
  deposit_waiver_min_role?: string;
  /** @deprecated Prefer pricing_matrix_view_roles / pricing_matrix_edit_roles */
  pricing_matrix_roles?: string[];
  pricing_matrix_view_roles?: string[];
  pricing_matrix_edit_roles?: string[];
  /** Per-settings-page view/edit role lists */
  settings_sections?: Partial<
    Record<
      string,
      {
        viewRoles?: string[];
        editRoles?: string[];
      }
    >
  >;
};

export const DEFAULT_FEATURE_FLAGS: StoreFeatureFlags = {
  discount_max_percent: 100,
  discount_require_pin_above: 20,
  discount_require_reason: true,
  void_allowed_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  refund_allowed_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  price_override_allowed: true,
  price_override_min_role: 'front_desk',
  price_override_require_pin: true,
  show_cost_prices_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  show_profit_margin_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  show_supplier_costs: false,
  dashboard_min_role: 'store_manager',
  reports_min_role: 'store_manager',
  financial_reports_min_role: 'tenant_admin',
  can_edit_customer_roles: ['front_desk', 'store_manager', 'tenant_admin', 'platform_admin'],
  can_delete_customer_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  customer_archive_view_roles: ['tenant_admin', 'store_manager'],
  inventory_archive_view_roles: ['tenant_admin', 'store_manager'],
  inventory_adjustment_min_role: 'store_manager',
  can_receive_po_roles: ['front_desk', 'store_manager', 'tenant_admin', 'platform_admin'],
  cash_session_min_role: 'front_desk',
  can_view_cash_report: ['store_manager', 'tenant_admin', 'platform_admin'],
  can_change_promised_date: true,
  can_waive_deposit: false,
  deposit_waiver_min_role: 'store_manager',
  pricing_matrix_view_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
  pricing_matrix_edit_roles: ['store_manager', 'tenant_admin', 'platform_admin'],
};

const ROLE_RANK: Record<string, number> = {
  read_only: 0,
  front_desk: 1,
  technician: 2,
  store_manager: 3,
  tenant_admin: 4,
  platform_admin: 5,
};

export function isManagerRole(role: string): boolean {
  return ['store_manager', 'tenant_admin', 'platform_admin'].includes(role);
}

export function roleMeetsMinimum(role: string, minRole: string): boolean {
  return (ROLE_RANK[role] ?? 0) >= (ROLE_RANK[minRole] ?? 99);
}

export function mergeFeatureFlags(raw: Record<string, unknown> | null | undefined): StoreFeatureFlags {
  return { ...DEFAULT_FEATURE_FLAGS, ...(raw ?? {}) };
}

/** Convert a flat discount amount into an effective percent of a base price. */
export function discountAmountToPct(amount: number, base: number): number {
  if (!(base > 0) || !(amount > 0)) return 0;
  return (amount / base) * 100;
}

export type DiscountEvaluation = {
  allowed: boolean;
  reason?: string;
  /** @deprecated Prefer needsManagerOverride for discount threshold */
  needsPin: boolean;
  /** Non-manager exceeded discount_max_percent — prompt manager override PIN */
  needsManagerOverride: boolean;
  reasonRequired: boolean;
};

/**
 * Evaluate store discount rules for a given effective discount %.
 * Over `discount_max_percent` for non-managers requires a manager override PIN
 * (not a hard block).
 */
export function evaluateDiscount(params: {
  flags: StoreFeatureFlags;
  userRole: string;
  discountPct: number;
  hasReason?: boolean;
}): DiscountEvaluation {
  const { flags, userRole, discountPct } = params;
  const pct = Math.max(0, Number(discountPct) || 0);
  const reasonRequired = flags.discount_require_reason ?? true;
  const maxPct = flags.discount_max_percent ?? 100;
  const needsManagerOverride = pct > 0 && pct > maxPct && !isManagerRole(userRole);

  if (pct > 0 && reasonRequired && !params.hasReason) {
    return {
      allowed: false,
      reason: 'Please enter a reason for this discount',
      needsPin: false,
      needsManagerOverride,
      reasonRequired,
    };
  }

  return {
    allowed: true,
    needsPin: false,
    needsManagerOverride,
    reasonRequired,
  };
}

export function checkPermission(params: {
  flags: StoreFeatureFlags;
  userRole: string;
  flag: string;
  value?: unknown;
}): { allowed: boolean; reason?: string } {
  const { flags, userRole, flag, value } = params;

  switch (flag) {
    case 'apply_discount': {
      const pct = typeof value === 'number' ? value : Number(value ?? 0);
      const result = evaluateDiscount({ flags, userRole, discountPct: pct, hasReason: true });
      if (result.needsManagerOverride) {
        const maxPct = flags.discount_max_percent ?? 100;
        return {
          allowed: false,
          reason: `Discounts over ${maxPct}% require manager approval`,
        };
      }
      return result.allowed
        ? { allowed: true }
        : { allowed: false, ...(result.reason ? { reason: result.reason } : {}) };
    }
    case 'view_dashboard': {
      const minRole = flags.dashboard_min_role ?? 'store_manager';
      return {
        allowed: roleMeetsMinimum(userRole, minRole),
        reason: 'Your role cannot view the dashboard',
      };
    }
    case 'void_ticket': {
      const allowed = flags.void_allowed_roles ?? DEFAULT_FEATURE_FLAGS.void_allowed_roles!;
      if (!allowed.includes(userRole)) {
        return { allowed: false, reason: 'Your role cannot void tickets' };
      }
      return { allowed: true };
    }
    case 'issue_refund': {
      const allowed = flags.refund_allowed_roles ?? DEFAULT_FEATURE_FLAGS.refund_allowed_roles!;
      if (!allowed.includes(userRole)) {
        return { allowed: false, reason: 'Your role cannot issue refunds' };
      }
      return { allowed: true };
    }
    case 'view_cost_prices': {
      const allowed = flags.show_cost_prices_roles ?? DEFAULT_FEATURE_FLAGS.show_cost_prices_roles!;
      return { allowed: allowed.includes(userRole) };
    }
    case 'view_profit_margin': {
      const allowed =
        flags.show_profit_margin_roles ?? DEFAULT_FEATURE_FLAGS.show_profit_margin_roles!;
      return { allowed: allowed.includes(userRole) };
    }
    case 'view_financial_reports': {
      const minRole = flags.financial_reports_min_role ?? 'tenant_admin';
      return { allowed: roleMeetsMinimum(userRole, minRole) };
    }
    case 'view_reports': {
      const minRole = flags.reports_min_role ?? 'store_manager';
      return { allowed: roleMeetsMinimum(userRole, minRole) };
    }
    case 'price_override': {
      if (flags.price_override_allowed === false) {
        return { allowed: false, reason: 'Price overrides are disabled for this store' };
      }
      const minRole = flags.price_override_min_role ?? 'front_desk';
      if (!roleMeetsMinimum(userRole, minRole)) {
        return { allowed: false, reason: 'Your role cannot override prices' };
      }
      return { allowed: true };
    }
    case 'inventory_adjustment': {
      const minRole = flags.inventory_adjustment_min_role ?? 'store_manager';
      if (!roleMeetsMinimum(userRole, minRole)) {
        return { allowed: false, reason: 'Your role cannot adjust inventory' };
      }
      return { allowed: true };
    }
    case 'waive_deposit': {
      if (!flags.can_waive_deposit) {
        return { allowed: false, reason: 'Deposit waivers are disabled' };
      }
      const minRole = flags.deposit_waiver_min_role ?? 'store_manager';
      if (!roleMeetsMinimum(userRole, minRole)) {
        return { allowed: false, reason: 'Your role cannot waive deposits' };
      }
      return { allowed: true };
    }
    case 'edit_customer': {
      const allowed =
        flags.can_edit_customer_roles ?? DEFAULT_FEATURE_FLAGS.can_edit_customer_roles!;
      return { allowed: allowed.includes(userRole) };
    }
    case 'delete_customer': {
      const allowed =
        flags.can_delete_customer_roles ?? DEFAULT_FEATURE_FLAGS.can_delete_customer_roles!;
      return { allowed: allowed.includes(userRole) };
    }
    case 'view_archived_customers': {
      const allowed =
        flags.customer_archive_view_roles ?? DEFAULT_FEATURE_FLAGS.customer_archive_view_roles!;
      return {
        allowed: allowed.includes(userRole) || userRole === 'platform_admin',
        reason: 'Your role cannot view archived customers',
      };
    }
    case 'view_archived_inventory': {
      const allowed =
        flags.inventory_archive_view_roles ?? DEFAULT_FEATURE_FLAGS.inventory_archive_view_roles!;
      return {
        allowed: allowed.includes(userRole) || userRole === 'platform_admin',
        reason: 'Your role cannot view archived inventory',
      };
    }
    case 'view_cash_report': {
      const allowed = flags.can_view_cash_report ?? DEFAULT_FEATURE_FLAGS.can_view_cash_report!;
      return { allowed: allowed.includes(userRole) };
    }
    case 'pricing_matrix_view': {
      const allowed =
        flags.pricing_matrix_view_roles ??
        flags.pricing_matrix_roles ??
        DEFAULT_FEATURE_FLAGS.pricing_matrix_view_roles!;
      return {
        allowed: allowed.includes(userRole) || userRole === 'platform_admin',
        reason: 'Your role cannot view the pricing matrix',
      };
    }
    case 'pricing_matrix_edit': {
      const allowed =
        flags.pricing_matrix_edit_roles ??
        flags.pricing_matrix_roles ??
        DEFAULT_FEATURE_FLAGS.pricing_matrix_edit_roles!;
      return {
        allowed: allowed.includes(userRole) || userRole === 'platform_admin',
        reason: 'Your role cannot edit the pricing matrix',
      };
    }
    default:
      return { allowed: true };
  }
}
